/**
 * GoogleAuth.js — Google OAuth redirect handler for the AdminPortal web flow.
 *
 * Handles the web-browser Google OAuth flow (backend-mediated):
 *   1. initiateGoogleLogin() — opens the server authorize route, which mints browser-bound state,
 *      then redirects to backend /oauth/google/authorize endpoint
 *   2. handleGoogleCallback(state, onSuccess) — validates state, fetches JWT from backend,
 *      calls onSuccess(jwtToken, user)
 *
 * Auth flow:
 *   User click → initiateGoogleLogin() → backend /oauth/google/authorize
 *   → accounts.google.com/o/oauth2/v2/auth
 *   → Google login → backend /oauth/google/callback → backend stores JWT keyed by state
 *   → backend redirects to web UI with ?google_state={state}
 *   → handleGoogleCallback(state, onSuccess) → GET /oauth/google/token?state={state} → JWT
 */

/**
 * Initiates the Google OAuth login flow for the web browser.
 *
 * Opens the backend authorize route, which mints and binds state,
 * then redirects the browser to the backend's /oauth/google/authorize endpoint.
 *
 * @returns {void}
 */
export function initiateGoogleLogin() {
    const apiBase = getApiBase();
    const clientRedirectUri = encodeURIComponent(`${window.location.origin}/login`);
    const authorizeUrl = `${apiBase}/oauth/google/authorize?client_redirect_uri=${clientRedirectUri}`;

    window.location.assign(authorizeUrl);
}

/**
 * Handles the Google OAuth callback after the backend redirects back to the web UI.
 *
 * Redeems the browser-bound state returned by the backend,
 * retrieving the JWT from the backend via GET /oauth/google/token?state={state},
 * then calls onSuccess(jwtToken, user).
 *
 * @param {string} state - The state parameter returned from the backend redirect
 * @param {function} onSuccess - Callback invoked with (jwtToken, user) on success
 * @returns {Promise<void>}
 * @throws {Error} When state is invalid/missing, or backend returns an error
 */
export async function handleGoogleCallback(state, onSuccess) {
    if (!state) throw new Error('Google OAuth completion code is missing.');

    const apiBase = getApiBase();
    const tokenUrl = `${apiBase}/oauth/google/token?state=${encodeURIComponent(state)}`;

    const response = await fetch(tokenUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Google OAuth token retrieval failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    if (onSuccess) {
        onSuccess(data.JwtToken, data.User);
    }
}

/**
 * Returns the API base URL for the Google OAuth endpoints.
 * The /oauth/google/* endpoints live on the API project (zoomapi-*.meetingsync.ai),
 * NOT the Functions project (func-*.meetingsync.ai).
 *
 * @returns {string} Base URL, e.g. "https://zoomapi-test.qolsync.ai"
 */
function getApiBase() {
    return process.env.REACT_APP_ZOOM_API_URL || 'https://zoomapi-test.qolsync.ai';
}
